import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, ChevronDown, GripVertical, Edit2, Lock, Unlock, BookMarked, FolderPlus, Trash2, Check, FileText, FileSpreadsheet } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from './ui/context-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

const PALETTE = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

// T-135: ícono de tipo de hoja (texto vs cálculo). Gris neutro — distinto del
// ámbar del candado de fuente. Reutilizado en todos los sitios donde se lista una hoja.
const SheetTypeIcon = ({ sheet, className = 'w-3.5 h-3.5 shrink-0 text-slate-400 dark:text-slate-500' }) =>
  sheet.content_type === 'spreadsheet'
    ? <FileSpreadsheet className={className} title="Hoja de cálculo" />
    : <FileText className={className} title="Hoja de texto" />;

const SheetTabs = ({
  sheets,
  activeSheetId,
  onSelectSheet,
  onAddSheet,
  onDeleteSheet,
  onReorderSheets,
  onReorderRow,
  onRenameSheet,
  onImportSheet,
  onToggleSource,
  controlsOnly = false,
  groups = [],
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  onAssignSheet,
}) => {
  // T-133: modelo de orden compartido. La fila única intercala hojas huérfanas
  // y chips de grupo. Arrastrar reordena la fila (persiste via onReorderRow);
  // arrastrar una hoja sobre un chip la asigna al grupo.
  const [draggedItem, setDraggedItem] = useState(null); // {type:'sheet'|'group', data}
  const [openGroupId, setOpenGroupId] = useState(null); // un popover abierto a la vez
  const didDragRef = useRef(false);
  const [renamingSheetId, setRenamingSheetId] = useState(null);
  const [newName, setNewName] = useState('');
  const [sheetToDelete, setSheetToDelete] = useState(null); // pending delete confirmation
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourcePage, setSourcePage] = useState(0);
  // T-127: estado de edición de grupos (rename, color, asignar, borrar).
  const [renamingGroupId, setRenamingGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');

  // Reset de la búsqueda de asignación cuando cambia el popover abierto.
  useEffect(() => {
    setAssignSearch('');
  }, [openGroupId]);

  // T-054: el input vive fuera del dropdown para que el click en el ítem lo
  // dispare de forma confiable en todos los navegadores.
  const importInputRef = useRef(null);

  const handleImportFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // permite reimportar el mismo archivo
    if (file && typeof onImportSheet === 'function') {
      onImportSheet(file);
    }
  };

  // Ask before deleting (destructive + irreversible). Guards the last-sheet rule
  // up front so the dialog never opens for it.
  const requestDelete = (sheet) => {
    if (sheets.length === 1) {
      toast.error('No puedes eliminar la última hoja');
      return;
    }
    setSheetToDelete(sheet);
  };

  const sourceCount = sheets.filter((s) => s.is_source).length;

  // --- Hojas fuente: búsqueda + paginado (T-#2) ---
  const sourceQ = sourceSearch.trim().toLowerCase();
  const sourceFiltered = sheets.filter((s) => (s.name || '').toLowerCase().includes(sourceQ));
  const SOURCE_PER_PAGE = 10;
  const sourcePageCount = Math.max(1, Math.ceil(sourceFiltered.length / SOURCE_PER_PAGE));
  const sourceCurrentPage = Math.min(sourcePage, sourcePageCount - 1);
  const sourcePageItems = sourceFiltered.slice(
    sourceCurrentPage * SOURCE_PER_PAGE,
    sourceCurrentPage * SOURCE_PER_PAGE + SOURCE_PER_PAGE
  );

  // Hojas huérfanas = group_id null o de grupo inexistente (fallback defensivo).
  const existingGroupIds = new Set(groups.map((g) => g.id));
  const isOrphan = (s) => !s.group_id || !existingGroupIds.has(s.group_id);

  // rowItems = huérfanas + grupos, ordenados por pos (tie-break created_at → id).
  const orphanSheets = sheets.filter(isOrphan);
  const rowItems = [
    ...orphanSheets.map((s) => ({ type: 'sheet', pos: s.order ?? 0, data: s })),
    ...groups.map((g) => ({ type: 'group', pos: g.group_order ?? 0, data: g })),
  ];
  rowItems.sort((a, b) => {
    if (a.pos !== b.pos) return a.pos - b.pos;
    const ca = a.data.created_at || '';
    const cb = b.data.created_at || '';
    if (ca !== cb) return ca < cb ? -1 : 1;
    const ida = String(a.data.id);
    const idb = String(b.data.id);
    return ida < idb ? -1 : ida > idb ? 1 : 0;
  });

  // --- Grupo: asignar hojas (checklist) ---
  const assignFiltered = sheets.filter((s) =>
    (s.name || '').toLowerCase().includes(assignSearch.trim().toLowerCase())
  );

  // --- Drag de hojas (fila única) ---
  const handleDragStart = (e, sheet) => {
    setDraggedItem({ type: 'sheet', data: sheet });
    e.dataTransfer.effectAllowed = 'move';
    // Marca el id de la hoja en el dataTransfer para que un drop externo
    // (p.ej. el panel de contexto del chat) pueda leerlo y añadirla.
    e.dataTransfer.setData('application/x-sheet-id', sheet.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // LIMITACIÓN (T-133): soltar una HOJA sobre un CHIP asigna (no reordena),
  // así que no puedes colocar una hoja exactamente ENTRE dos chips adyacentes
  // sin una hoja vecina; para reposicionar, suelta sobre una hoja. OK para fase 1.
  const handleRowDrop = (e, targetItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedItem) return;

    // CASO ASIGNAR: hoja arrastrada sobre un chip de grupo → meter al grupo.
    if (draggedItem.type === 'sheet' && targetItem.type === 'group') {
      if ((draggedItem.data.group_id ?? null) !== targetItem.data.id) {
        onAssignSheet?.(draggedItem.data.id, targetItem.data.id);
      }
      setDraggedItem(null);
      return;
    }

    // CASO REORDENAR: cualquier otro drop entre row items.
    if (draggedItem.data.id === targetItem.data.id) {
      setDraggedItem(null);
      return;
    }
    const items = rowItems.slice();
    const from = items.findIndex(
      (i) => i.type === draggedItem.type && i.data.id === draggedItem.data.id
    );
    const to = items.findIndex(
      (i) => i.type === targetItem.type && i.data.id === targetItem.data.id
    );
    if (from < 0 || to < 0) {
      setDraggedItem(null);
      return;
    }
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    const reindexed = items.map((it, idx) => ({ ...it, pos: idx }));
    onReorderRow?.(reindexed);
    setDraggedItem(null);
  };

  const handleRenameStart = (sheet) => {
    setRenamingSheetId(sheet.id);
    setNewName(sheet.name);
  };

  const handleRenameSave = async (sheetId) => {
    if (!newName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    await onRenameSheet(sheetId, newName);
    setRenamingSheetId(null);
    setNewName('');
  };

  // --- Grupo: rename (mismo patrón que las hojas) ---
  const handleGroupRenameStart = (group) => {
    setRenamingGroupId(group.id);
    setNewGroupName(group.name);
  };
  const handleGroupRenameSave = (groupId) => {
    if (!newGroupName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    onUpdateGroup?.(groupId, { name: newGroupName });
    setRenamingGroupId(null);
    setNewGroupName('');
  };

  // --- Pestaña de hoja reutilizable (fila única) ---
  const renderSheetTab = (sheet) => (
    <ContextMenu key={sheet.id}>
      <ContextMenuTrigger asChild>
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, sheet)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleRowDrop(e, { type: 'sheet', data: sheet })}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-move transition-all whitespace-nowrap ${
            activeSheetId === sheet.id
              ? 'bg-sky-500 text-white shadow-lg'
              : 'bg-white/20 text-slate-700 hover:bg-white/30 dark:bg-slate-700/40 dark:text-slate-200 dark:hover:bg-slate-600/50'
          }`}
        >
          <GripVertical className="w-4 h-4 opacity-50" />
          <SheetTypeIcon sheet={sheet} />
          {sheet.is_source && (
            <Lock
              className="w-3.5 h-3.5 text-amber-400 shrink-0"
              title="Hoja fuente (solo lectura para la IA)"
            />
          )}
          <button
            onClick={() => onSelectSheet(sheet.id)}
            onDoubleClick={() => handleRenameStart(sheet)}
            title="Doble clic para renombrar"
            className="flex-1 text-sm font-medium"
          >
            {sheet.name}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              requestDelete(sheet);
            }}
            className="hover:bg-white/20 p-1 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => onSelectSheet(sheet.id)}>
          Abrir
        </ContextMenuItem>
        <ContextMenuItem onClick={() => handleRenameStart(sheet)}>
          <Edit2 className="w-4 h-4 mr-2" />
          Renombrar
        </ContextMenuItem>
        <ContextMenuItem onClick={() => onToggleSource?.(sheet)}>
          {sheet.is_source ? (
            <><Unlock className="w-4 h-4 mr-2" />Quitar fuente</>
          ) : (
            <><Lock className="w-4 h-4 mr-2" />Marcar como fuente</>
          )}
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => requestDelete(sheet)}
          className="text-red-600 dark:text-red-400"
        >
          Eliminar
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );

  // --- Chip de grupo (fila única) + dropdown flotante (T-133) ---
  const renderGroupChip = (group) => {
    const groupSheets = sheets
      .filter((s) => s.group_id === group.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return (
      <Popover
        key={group.id}
        open={openGroupId === group.id}
        onOpenChange={(o) => {
          if (o && didDragRef.current) return; // no abrir tras arrastrar
          setOpenGroupId(o ? group.id : null);
        }}
      >
        <PopoverTrigger asChild>
          <div
            draggable
            onDragStart={(e) => {
              didDragRef.current = true;
              setOpenGroupId(null);
              setDraggedItem({ type: 'group', data: group });
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('application/x-group-id', group.id);
            }}
            onDragEnd={() => {
              setTimeout(() => { didDragRef.current = false; }, 0);
              setDraggedItem(null);
            }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleRowDrop(e, { type: 'group', data: group })}
            style={{ borderColor: group.color || '#3b82f6' }}
            className="flex items-center gap-1 px-2 py-2 rounded-lg border-l-4 bg-white/20 dark:bg-slate-700/40 cursor-move whitespace-nowrap"
          >
            <span className="text-sm font-semibold px-1">{group.name}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">({groupSheets.length})</span>
            <ChevronDown className="w-4 h-4 opacity-70" />
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2">
          {/* Header: rename + color + borrar */}
          <div className="flex items-center gap-1 mb-2 pb-2 border-b border-slate-200 dark:border-slate-700">
            {renamingGroupId === group.id ? (
              <Input
                autoFocus
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onBlur={() => handleGroupRenameSave(group.id)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleGroupRenameSave(group.id);
                  if (e.key === 'Escape') setRenamingGroupId(null);
                }}
                className="h-6 px-2 text-sm flex-1"
              />
            ) : (
              <button
                onDoubleClick={() => handleGroupRenameStart(group)}
                title="Doble clic para renombrar"
                className="text-sm font-semibold px-1 py-0.5 rounded hover:bg-white/20 dark:hover:bg-slate-700/50 flex-1 text-left truncate"
              >
                {group.name}
              </button>
            )}
            {/* Color: popover con swatches */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  title="Cambiar color"
                  className="p-1 rounded hover:bg-white/20 dark:hover:bg-slate-700/50"
                >
                  <span
                    className="block w-4 h-4 rounded-full border border-white/40"
                    style={{ backgroundColor: group.color || '#3b82f6' }}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-2">
                <div className="flex gap-1">
                  {PALETTE.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => {
                        onUpdateGroup?.(group.id, { color: c });
                      }}
                      className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                      style={{
                        backgroundColor: c,
                        borderColor: group.color === c ? '#fff' : 'transparent',
                      }}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {/* Borrar grupo */}
            <button
              type="button"
              title="Eliminar grupo"
              onClick={() => setGroupToDelete(group)}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Asignar hojas — sección con búsqueda + checklist */}
          <div className="mb-2 pb-2 border-b border-slate-200 dark:border-slate-700">
            <div className="px-1 py-1 text-xs text-slate-500 dark:text-slate-400">
              Marca las hojas que pertenecen a este grupo.
            </div>
            <Input
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              placeholder="Buscar hoja…"
              className="h-8 text-sm mb-2"
            />
            {assignFiltered.map((sheet) => {
              const inGroup = sheet.group_id === group.id;
              return (
                <button
                  type="button"
                  key={sheet.id}
                  onClick={() => onAssignSheet?.(sheet.id, inGroup ? null : group.id)}
                  className="w-full cursor-pointer flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    <SheetTypeIcon sheet={sheet} />
                    <span className="truncate">{sheet.name}</span>
                    {sheet.is_source && <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" title="Hoja fuente (solo lectura para la IA)" />}
                  </span>
                  <Check
                    className={`w-4 h-4 shrink-0 ${inGroup ? 'text-sky-500' : 'opacity-20'}`}
                  />
                </button>
              );
            })}
            {assignFiltered.length === 0 && (
              <div className="px-2 py-2 text-xs text-slate-400">Sin resultados.</div>
            )}
          </div>

          {/* Lista vertical de las hojas del grupo */}
          <div className="flex flex-col gap-1">
            {groupSheets.length === 0 ? (
              <div className="px-2 py-2 text-xs text-slate-400">Este grupo no tiene hojas.</div>
            ) : (
              groupSheets.map((sheet) => (
                <button
                  type="button"
                  key={sheet.id}
                  onClick={() => onSelectSheet(sheet.id)}
                  className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-sm cursor-pointer ${
                    activeSheetId === sheet.id
                      ? 'bg-sky-500 text-white'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200'
                  }`}
                >
                  <SheetTypeIcon sheet={sheet} />
                  {sheet.is_source && <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0" title="Hoja fuente (solo lectura para la IA)" />}
                  <span className="truncate flex-1 text-left">{sheet.name}</span>
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div
      className={controlsOnly
        ? 'flex items-center gap-2 shrink-0'
        : 'sheet-tabs-container flex items-center gap-2 bg-white/10 p-2 rounded-lg border border-white/20'}
    >
      {!controlsOnly && (
        <div className="flex items-center gap-1 flex-1 overflow-x-auto">
          {rowItems.map((item) =>
            item.type === 'group' ? renderGroupChip(item.data) : renderSheetTab(item.data)
          )}
        </div>
      )}

      {/* Add Sheet Button — T-049: dropdown to pick text vs spreadsheet sheet */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
            title="Nueva hoja"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline text-[10px]">Nueva hoja</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onAddSheet('text')} className="cursor-pointer">
            Nueva hoja de texto
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddSheet('spreadsheet')} className="cursor-pointer">
            Nueva hoja de cálculo
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => importInputRef.current?.click()} className="cursor-pointer">
            Importar documento…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onCreateGroup?.()} className="cursor-pointer">
            <FolderPlus className="w-4 h-4 mr-2" />
            Nuevo grupo
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={importInputRef}
        type="file"
        accept=".md,.markdown,.txt,.docx,.pdf"
        hidden
        onChange={handleImportFileChange}
      />

      {/* Hojas fuente: botón + popover con búsqueda y paginado. Una hoja
          fuente entra al contexto de la IA pero queda bloqueada contra su edición. */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-white/20 border-white/30 hover:bg-white/30"
            title="Hojas fuente (solo lectura para la IA)"
          >
            <BookMarked className="w-4 h-4" />
            <span className="hidden sm:inline text-[10px]">Nueva fuente</span>
            {sourceCount > 0 && (
              <span className="ml-1 text-xs font-semibold">{sourceCount}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-2">
          <div className="px-1 py-1 text-xs text-slate-500 dark:text-slate-400">
            Las hojas fuente entran al contexto de la IA pero no puede editarlas.
          </div>
          <Input
            value={sourceSearch}
            onChange={(e) => { setSourceSearch(e.target.value); setSourcePage(0); }}
            placeholder="Buscar hoja…"
            className="h-8 text-sm mb-2"
          />
          {sourcePageItems.map((sheet) => (
            <button
              type="button"
              key={sheet.id}
              onClick={() => onToggleSource?.(sheet)}
              className="w-full cursor-pointer flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <SheetTypeIcon sheet={sheet} />
                <span className="truncate">{sheet.name}</span>
              </span>
              {sheet.is_source ? (
                <Lock className="w-4 h-4 text-amber-500 shrink-0" />
              ) : (
                <Unlock className="w-4 h-4 opacity-30 shrink-0" />
              )}
            </button>
          ))}
          {sourceFiltered.length === 0 && (
            <div className="px-2 py-2 text-xs text-slate-400">Sin resultados.</div>
          )}
          {sourcePageCount > 1 && (
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-200 dark:border-slate-700 text-xs">
              <button
                type="button"
                disabled={sourceCurrentPage === 0}
                onClick={() => setSourcePage((p) => Math.max(0, p - 1))}
                className="px-2 py-1 rounded disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Anterior
              </button>
              <span>Página {sourceCurrentPage + 1} de {sourcePageCount}</span>
              <button
                type="button"
                disabled={sourceCurrentPage >= sourcePageCount - 1}
                onClick={() => setSourcePage((p) => Math.min(sourcePageCount - 1, p + 1))}
                className="px-2 py-1 rounded disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Siguiente
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Confirm before deleting a sheet (destructive, irreversible). */}
      <AlertDialog open={!!sheetToDelete} onOpenChange={(open) => !open && setSheetToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la hoja «{sheetToDelete?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la hoja y todo su contenido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (sheetToDelete) onDeleteSheet(sheetToDelete.id);
                setSheetToDelete(null);
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* T-127: confirmar antes de borrar un grupo (las hojas quedan huérfanas). */}
      <ConfirmDialog
        open={!!groupToDelete}
        onOpenChange={(open) => !open && setGroupToDelete(null)}
        title="¿Eliminar el grupo?"
        description="Las hojas del grupo no se eliminan, quedan sin grupo. Esta acción no se puede deshacer."
        confirmText="Eliminar grupo"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={() => {
          if (groupToDelete) onDeleteGroup?.(groupToDelete.id);
          setGroupToDelete(null);
        }}
      />

      {/* T-141: renombrar hoja en modal (no inline) para que la pestaña no se
          encoja cuando hay muchas hojas y la barra está expandida. */}
      <Dialog
        open={renamingSheetId != null}
        onOpenChange={(o) => {
          if (!o) {
            setRenamingSheetId(null);
            setNewName('');
          }
        }}
      >
        <DialogContent className="glass border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle>Renombrar hoja</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleRenameSave(renamingSheetId);
            }}
            placeholder="Nombre de la hoja"
            className="text-sm"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRenamingSheetId(null);
                setNewName('');
              }}
            >
              Cancelar
            </Button>
            <Button onClick={() => handleRenameSave(renamingSheetId)}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SheetTabs;
